import Tabs from 'antd/lib/tabs';
import React, { useMemo, useState } from 'react';
import {
    ICreateSavedFilter,
    SavedFilterStatus,
    SavedFilterSubType,
    SavedFilterType,
} from '../../../components/sidebar-filters/saved-filter/interface';
import { SidebarFilterActions, TSidebarActions } from '../../../components/sidebar-filters/SidebarFilterActions';
import { SITES_PAGE_CONST } from './consts';
import { SitesSidebarFilters } from './SitesSidebarFilters';
import { ISitesSidebarFilters, useSitesSidebarFiltersContext } from './SitesSidebarFiltersContext';

import { SitesSidebarSaved } from './SitesSidebarSaved';
import { useCustomizableTableContext } from 'src/components/table/CustomizableTableContext';

type TSidebarTab = 'filters' | 'saved' | 'grid';

type TSiteTabItem = {
    key: TSidebarTab;
    label: React.ReactNode;
    children: React.ReactNode;
};

interface ISitesSidebarTabsProps {
    onOpenSaveFiltersModal: (savedFilterData: ICreateSavedFilter | null) => void;
    onFilterChange: (siteFilters: ISitesSidebarFilters) => void;
    gridComponent?: React.ReactNode; // Optional prop for custom grid component
}

export const SitesSidebarTabs: React.FC<ISitesSidebarTabsProps> = ({ onOpenSaveFiltersModal, onFilterChange, gridComponent }) => {
    const [activeTab, setActiveTab] = useState<TSidebarTab>('filters');

    const handleTabChange = (key: string) => {
        setActiveTab(key as TSidebarTab);
    };

    const tabs: TSiteTabItem[] = [
        {
            key: 'filters',
            label: <FiltersLabel />,
            children: (
                <TabContentWrapper activeTab={activeTab} onOpenSaveFiltersModal={onOpenSaveFiltersModal}>
                    <SitesSidebarFilters onFilterChange={onFilterChange} />
                </TabContentWrapper>
            ),
        },
        {
            key: 'grid',
            label: <GridLabel />,
            children: (
                <TabContentWrapper activeTab={activeTab} onOpenSaveFiltersModal={onOpenSaveFiltersModal}>
                    {gridComponent}
                </TabContentWrapper>
            ),
        },
        {
            key: 'saved',
            label: <SavedLabel />,
            children: (
                <TabContentWrapper activeTab={activeTab} onOpenSaveFiltersModal={onOpenSaveFiltersModal}>
                    <SitesSidebarSaved onOpenSaveFiltersModal={onOpenSaveFiltersModal} />
                </TabContentWrapper>
            ),
        },
    ];

    return (
        <Tabs
            activeKey={activeTab}
            onChange={handleTabChange}
            defaultActiveKey="filters"
            destroyOnHidden={false}
            items={tabs}
            tabBarStyle={{ marginBottom: 0, padding: '0px 5px' }}
        />
    );
};

// This components helps to less re-rendering and improve performances
const FiltersLabel: React.FC = () => {
    const { siteFiltersCount } = useSitesSidebarFiltersContext();

    return <span>Filters {siteFiltersCount > 0 ? `(${siteFiltersCount})` : ''}</span>;
};

const SavedLabel: React.FC = () => {
    const { savedFilters } = useSitesSidebarFiltersContext();
    const savedFiltersCount = useMemo(() => savedFilters.length, [savedFilters]);

    return <span>Saved {savedFiltersCount > 0 ? `(${savedFiltersCount})` : ''}</span>;
};

const GridLabel: React.FC = () => {
    const { hiddenColumnsCount } = useCustomizableTableContext();

    return <span>Grid {hiddenColumnsCount > 0 ? `(${hiddenColumnsCount})` : ''}</span>;
};

interface ITabContentWrapperProps {
    children: React.ReactNode;
    activeTab: TSidebarTab;
    onOpenSaveFiltersModal: (savedFilterData: ICreateSavedFilter | null) => void;
}

const TabContentWrapper: React.FC<ITabContentWrapperProps> = ({ children, activeTab, onOpenSaveFiltersModal }) => {
    const { resetColumns } = useCustomizableTableContext();
    const { handleSiteFiltersClear, siteFiltersCount, siteFilters } = useSitesSidebarFiltersContext();

    const handleSaveFiltersClick = () => {
        onOpenSaveFiltersModal({
            id: null,
            filter_name: '',
            description: '',
            filter_values: siteFilters,
            status: SavedFilterStatus.active,
            type: SavedFilterType.filter,
            sub_type: SavedFilterSubType.sites,
        });
    };

    const shownActions: TSidebarActions[] = useMemo(() => {
        if (activeTab === 'filters') return ['saveFilters', 'clearFilters'];
        if (activeTab === 'saved') return ['clearFilters'];
        if (activeTab === 'grid') return ['resetGrid'];

        return [];
    }, [activeTab]);

    return (
        <div style={{ height: SITES_PAGE_CONST.tabContentHeight, display: 'flex', flexDirection: 'column' }}>
            <div
                style={{
                    flex: 1,
                    overflowY: 'auto',
                    minHeight: 0,
                }}
            >
                {children}
            </div>
            <SidebarFilterActions
                shownActions={shownActions}
                isSaveDisabled={siteFiltersCount === 0}
                onSaveFilters={handleSaveFiltersClick}
                onClearFilters={handleSiteFiltersClear}
                onResetGrid={resetColumns}
            />
        </div>
    );
};
